import { useLayoutEffect, useRef, useState } from 'react'

export function useElementSize<T extends Element>(): [
  React.RefObject<T | null>,
  { width: number; height: number },
] {
  const ref = useRef<T | null>(null)
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  })

  // useLayoutEffect so the initial measurement lands before first paint:
  // consumers position content off the measured size (e.g. the facesheet
  // content's sticky-header padding), and an after-paint measurement shows
  // one frame laid out against size 0.
  useLayoutEffect(() => {
    if (!ref.current) {
      return
    }
    const handleResize = (entries: ResizeObserverEntry[]): void => {
      setSize({
        width: entries[0].contentRect.width,
        height: entries[0].contentRect.height,
      })
    }
    const observer = new ResizeObserver(handleResize)
    observer.observe(ref.current)
    setSize({
      width: ref.current instanceof HTMLElement ? ref.current.offsetWidth : 0,
      height: ref.current instanceof HTMLElement ? ref.current.offsetHeight : 0,
    })
    return () => observer.disconnect()
  }, [])

  return [ref, size]
}
